import React, { useState, useRef, useCallback } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || '';

const DOC_TYPES = [
  'Αγωγή',
  'Εξώδικο',
  'Υπόμνημα',
  'Απάντηση σε Αγωγή',
  'Έφεση',
  'Αίτηση Ασφαλιστικών Μέτρων',
];

export default function Syntaxi({ token }) {
  const [form, setForm] = useState({
    doc_type: 'Αγωγή',
    enagon: '',
    enagomenos: '',
    sachverhalt: '',
    aitima: '',
    dikastirio: '',
    prothesmia: '',
    extra_notes: '',
  });

  const [phase, setPhase] = useState('idle');
  const [docsFound, setDocsFound] = useState(0);
  const [docs, setDocs] = useState([]);
  const [output, setOutput] = useState('');
  const [editableOutput, setEditableOutput] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [emailModal, setEmailModal] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailStatus, setEmailStatus] = useState('');
  const outputRef = useRef(null);

  const handleChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const currentText = editMode ? editableOutput : output;

  const handleGenerate = useCallback(async () => {
    if (!form.enagon.trim() || !form.enagomenos.trim() || !form.sachverhalt.trim() || !form.aitima.trim()) {
      setErrorMsg('Συμπλήρωσε: Ενάγων, Εναγόμενος, Πραγματικά Περιστατικά, Αίτημα');
      return;
    }
    setErrorMsg('');
    setOutput('');
    setEditableOutput('');
    setEditMode(false);
    setDocs([]);
    setDocsFound(0);
    setPhase('searching');

    try {
      const resp = await fetch(`${API_BASE}/api/syntaxi/generate-stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(form),
      });

      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let fullText = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop();

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const msg = JSON.parse(line.slice(6));
            if (msg.type === 'docs_found') { setDocsFound(msg.count); setPhase('streaming'); }
            else if (msg.type === 'docs') { setDocs(msg.docs); }
            else if (msg.type === 'token') {
              fullText += msg.text;
              setOutput(fullText);
              if (outputRef.current) outputRef.current.scrollTop = outputRef.current.scrollHeight;
            }
            else if (msg.type === 'done') { setEditableOutput(fullText); setPhase('done'); }
            else if (msg.type === 'error') { setErrorMsg(msg.message); setPhase('error'); }
          } catch {}
        }
      }
    } catch (err) {
      setErrorMsg(err.message);
      setPhase('error');
    }
  }, [form, token]);

  const handleCopy = () => navigator.clipboard.writeText(currentText);

  const handlePrint = () => {
    const win = window.open('', '_blank');
    win.document.write(`<html><head><title>${form.doc_type}</title>
      <style>body{font-family:'Times New Roman',serif;font-size:12pt;margin:2.5cm;line-height:1.8}pre{white-space:pre-wrap;font-family:inherit}</style>
      </head><body><pre>${currentText}</pre></body></html>`);
    win.document.close();
    win.print();
  };

  const handleSaveTxt = () => {
    const blob = new Blob([currentText], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${form.doc_type}_${form.enagon.replace(/\s/g,'_')}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleSavePdf = () => {
    const win = window.open('', '_blank');
    win.document.write(`<html><head><title>${form.doc_type}</title>
      <style>body{font-family:'Times New Roman',serif;font-size:12pt;margin:2.5cm;line-height:1.8}pre{white-space:pre-wrap;font-family:inherit}</style>
      </head><body><pre>${currentText}</pre>
      <script>window.onload=function(){window.print();window.onafterprint=function(){window.close();};};<\/script>
      </body></html>`);
    win.document.close();
  };

  const handleEmailSend = async () => {
    if (!emailTo.trim()) return;
    setEmailSending(true);
    try {
      const resp = await fetch(`${API_BASE}/api/syntaxi/send-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ to: emailTo, subject: `${form.doc_type} — ${form.enagon} κατά ${form.enagomenos}`, body: currentText }),
      });
      if (resp.ok) { setEmailStatus('✅ Στάλθηκε!'); setTimeout(() => { setEmailModal(false); setEmailStatus(''); setEmailTo(''); }, 2000); }
      else { window.location.href = `mailto:${emailTo}?subject=${encodeURIComponent(`${form.doc_type} — ${form.enagon} κατά ${form.enagomenos}`)}&body=${encodeURIComponent(currentText)}`; setEmailModal(false); }
    } catch {
      window.location.href = `mailto:${emailTo}?subject=${encodeURIComponent(`${form.doc_type} — ${form.enagon} κατά ${form.enagomenos}`)}&body=${encodeURIComponent(currentText)}`;
      setEmailModal(false);
    }
    setEmailSending(false);
  };

  const isLoading = phase === 'searching' || phase === 'streaming';

  return (
    <div className="syntaxi-container">
      <div className="syntaxi-header">
        <h2>✍️ Σύνταξη Νομικού Εγγράφου</h2>
        <p className="syntaxi-subtitle">Αυτόματη σύνταξη με βάση τη νομολογία — πρώτο σχέδιο έτοιμο σε δευτερόλεπτα</p>
      </div>

      <div className="syntaxi-layout">
        <div className="syntaxi-form-panel">
          <div className="syntaxi-field">
            <label>Τύπος Εγγράφου</label>
            <div className="doc-type-grid">
              {DOC_TYPES.map((dt) => (
                <button key={dt} className={`doc-type-btn ${form.doc_type === dt ? 'active' : ''}`}
                  onClick={() => setForm((f) => ({ ...f, doc_type: dt }))} disabled={isLoading}>{dt}</button>
              ))}
            </div>
          </div>

          <div className="syntaxi-row">
            <div className="syntaxi-field">
              <label>Ενάγων / Εντολέας *</label>
              <input name="enagon" value={form.enagon} onChange={handleChange} placeholder="Ονοματεπώνυμο ή επωνυμία" disabled={isLoading} />
            </div>
            <div className="syntaxi-field">
              <label>Εναγόμενος / Αντίδικος *</label>
              <input name="enagomenos" value={form.enagomenos} onChange={handleChange} placeholder="Ονοματεπώνυμο ή επωνυμία" disabled={isLoading} />
            </div>
          </div>

          <div className="syntaxi-row">
            <div className="syntaxi-field">
              <label>Δικαστήριο</label>
              <input name="dikastirio" value={form.dikastirio} onChange={handleChange} placeholder="π.χ. Μονομελές Πρωτοδικείο Θεσσαλονίκης" disabled={isLoading} />
            </div>
            <div className="syntaxi-field">
              <label>Δικάσιμος / Προθεσμία</label>
              <input name="prothesmia" value={form.prothesmia} onChange={handleChange} placeholder="π.χ. 15/04/2026" disabled={isLoading} />
            </div>
          </div>

          <div className="syntaxi-field">
            <label>Πραγματικά Περιστατικά *</label>
            <textarea name="sachverhalt" value={form.sachverhalt} onChange={handleChange}
              placeholder="Περιγράψτε αναλυτικά τα γεγονότα, τις ημερομηνίες, τις σχέσεις μεταξύ των μερών..." rows={6} disabled={isLoading} />
          </div>

          <div className="syntaxi-field">
            <label>Αίτημα *</label>
            <textarea name="aitima" value={form.aitima} onChange={handleChange}
              placeholder="Τι ζητάτε από το δικαστήριο / τον αποδέκτη..." rows={3} disabled={isLoading} />
          </div>

          <div className="syntaxi-field">
            <label>Πρόσθετες Οδηγίες <span className="optional">(προαιρετικό)</span></label>
            <textarea name="extra_notes" value={form.extra_notes} onChange={handleChange}
              placeholder="π.χ. Έμφαση σε αδικοπραξία..." rows={2} disabled={isLoading} />
          </div>

          {errorMsg && <div className="syntaxi-error">⚠️ {errorMsg}</div>}

          <button className={`syntaxi-submit ${isLoading ? 'loading' : ''}`} onClick={handleGenerate} disabled={isLoading}>
            {phase === 'searching' && '🔍 Αναζήτηση νομολογίας...'}
            {phase === 'streaming' && `✍️ Σύνταξη εγγράφου... (${docsFound} πηγές)`}
            {(phase === 'idle' || phase === 'done' || phase === 'error') && `✍️ Σύνταξη ${form.doc_type}`}
          </button>
        </div>

        {(output || phase === 'searching') && (
          <div className="syntaxi-output-panel">
            <div style={{background:'#EFF6FF',border:'1px solid #BFDBFE',borderRadius:8,padding:'8px 12px',margin:'0 0 12px 0',fontSize:12,color:'#1E3A8A',fontWeight:600}}>
              🤖 Το έγγραφο δημιουργήθηκε από τεχνητή νοημοσύνη (AI) και απαιτεί έλεγχο από δικηγόρο.
            </div>
            {docs.length > 0 && (
              <div className="syntaxi-sources">
                <span className="sources-label">📚 Νομολογία ({docs.length})</span>
                <div className="sources-list">
                  {docs.map((d) => (
                    <span key={d.id} className="source-chip">
                      {d.url ? <a href={d.url} target="_blank" rel="noopener noreferrer">📄 {d.decision || d.title?.slice(0,40)}</a>
                               : <span>📄 {d.decision || d.title?.slice(0,40)}</span>}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="syntaxi-text-header">
              <span className="doc-type-label">{form.doc_type}</span>
              {phase === 'done' && (
                <div className="output-actions">
                  <button onClick={() => { setEditMode(!editMode); if (!editMode) setEditableOutput(output); }}
                    className={`action-btn ${editMode ? 'action-btn-active' : ''}`}>
                    {editMode ? '✅ Αποθήκευση' : '✏️ Επεξεργασία'}
                  </button>
                  <button onClick={handleCopy} className="action-btn">📋 Αντιγραφή</button>
                  <button onClick={handlePrint} className="action-btn">🖨️ Εκτύπωση</button>
                  <button onClick={handleSavePdf} className="action-btn">📄 PDF</button>
                  <button onClick={handleSaveTxt} className="action-btn">💾 .txt</button>
                  <button onClick={() => setEmailModal(true)} className="action-btn">📧 Email</button>
                </div>
              )}
            </div>

            <div className="syntaxi-output" ref={outputRef}>
              {phase === 'searching' && !output && (
                <div className="searching-indicator"><span className="pulse">🔍</span> Αναζήτηση νομολογίας...</div>
              )}
              {editMode ? (
                <textarea className="syntaxi-edit-textarea" value={editableOutput}
                  onChange={(e) => setEditableOutput(e.target.value)} />
              ) : (
                <>
                  <pre className="syntaxi-pre">{output}</pre>
                  {phase === 'streaming' && <span className="cursor">▌</span>}
                </>
              )}
            </div>

            {phase === 'done' && (
              <div className="syntaxi-disclaimer">
                ⚠️ Προσχέδιο — χρήζει ελέγχου από δικηγόρο πριν υποβολή.
              </div>
            )}
          </div>
        )}
      </div>

      {emailModal && (
        <div className="syntaxi-modal-overlay" onClick={() => setEmailModal(false)}>
          <div className="syntaxi-modal" onClick={e => e.stopPropagation()}>
            <h3>📧 Αποστολή με Email</h3>
            <div className="syntaxi-field" style={{marginBottom:'12px'}}>
              <label>Παραλήπτης</label>
              <input type="email" value={emailTo} onChange={e => setEmailTo(e.target.value)}
                placeholder="email@example.com" onKeyDown={e => e.key === 'Enter' && handleEmailSend()} />
            </div>
            <div style={{fontSize:'0.83rem',color:'#666',marginBottom:'12px'}}>
              <strong>Θέμα:</strong> {form.doc_type} — {form.enagon} κατά {form.enagomenos}
            </div>
            {emailStatus && <div style={{color:'green',marginBottom:'8px'}}>{emailStatus}</div>}
            <div style={{display:'flex',gap:'8px',justifyContent:'flex-end'}}>
              <button onClick={() => setEmailModal(false)} className="action-btn">Ακύρωση</button>
              <button onClick={handleEmailSend} disabled={emailSending || !emailTo.trim()} className="syntaxi-submit"
                style={{padding:'8px 20px',fontSize:'0.9rem',marginTop:0}}>
                {emailSending ? 'Αποστολή...' : '📧 Αποστολή'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
